import { createContext, useCallback, useContext } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { environment, inLocalDevelopment } from "modules/environments"
import { useSite } from "modules/sites"
import { useGetData, usePostData } from "services/auth"
import { usePostData as usePostSales } from "services/sales"
import Cookies from "universal-cookie"
import { getLocalISOString } from "utils/date"
import { handleResponse } from "utils/fetch"

const cookies = new Cookies()

export const UserContext = createContext({})
UserContext.displayName = "UserContext"

export const ContextCombined = createContext({})

export const ProvideCombinedContext = (props) => {
  return (
    <UserContext.Consumer>
      {(context1) => <ContextCombined.Provider value={{ context1 }}>{props.children}</ContextCombined.Provider>}
    </UserContext.Consumer>
  )
}

export const useUser = () => {
  const { user } = useContext(UserContext)
  return user
}

export const useCurrency = () => {
  const { user } = useContext(UserContext)
  return user?.currencyInfo || user?.CurrencyInfo
}

const getDeviceIdentity = () => {
  let deviceIdentity = cookies.get("browserInfo", { domain: ".gogoro.com", path: "/" })
  if (!deviceIdentity) {
    deviceIdentity = Math.random().toString(36).substring(2)
    cookies.set("browserInfo", deviceIdentity, { path: "/", maxAge: 5184000 }) // two months
  }
  return deviceIdentity
}

export const useAuth = () => {
  const site = useSite()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { setUser } = useContext(UserContext)
  const getUser = useGetData(
    site.name === "sales" ? "/tw/api/account/get-current-user" : "/api/utility/account/get-current-user",
  )
  const logOutLegacy = usePostData(site.name === "sales" ? "/tw/api/account/logout" : "/api/utility/account/logout")
  const logOutUser = useGetData("/api/utility/account/logout")

  const logInService = usePostData("/api/utility/account/login")
  const logInService2FA = usePostData("/api/utility/account/two-factor-login")
  const selectService2FA = usePostData("/api/utility/account/get-otp")
  const verifyServiceOTP = usePostData("/api/utility/account/verify-otp")

  const logInUser = usePostData("/api/auth/sneak-login")
  const getAuth = useGetData("/api/auth/check-login")

  const fetchUser = useCallback(
    () =>
      getUser()
        .then(handleResponse)
        .then((response) => {
          const user = site.name === "sales" ? response.Data : response.data
          // it may be empty object, so check the keys
          return user && Object.keys(user).length ? user : null
        }),
    [site, getUser],
  )

  const getAuthURL = useCallback(
    () =>
      getAuth(window.location.pathname !== "/" ? { path: window.location.pathname } : undefined).then(
        (response) => response.redirectUrl,
      ),
    [getAuth],
  )

  const logIn = useCallback(
    ({ accountId, password }) => logInUser({ username: accountId, password }).then(fetchUser).then(setUser),
    [logInUser, fetchUser, setUser],
  )

  const logOut = useCallback(
    () => (["sales", "franchise"].includes(site.name) ? logOutLegacy : logOutUser)().then(() => setUser(null)),
    [site, logOutLegacy, logOutUser, setUser],
  )

  const franchiseAuth = {
    logIn: useCallback(
      ({ accountId, password }) => {
        const pass2FA = inLocalDevelopment || (environment !== "production" && accountId[0] === "!")
        const account = accountId.replace(/^!/, "")
        return (
          pass2FA
            ? logInService({ account, password })
            : Promise.resolve(executeRecaptcha("login")).then((token) =>
                logInService2FA({
                  // executeRecaptcha() is a function
                  account,
                  password,
                  deviceIdentity: getDeviceIdentity(),
                  recaptchaResponse: token,
                }),
              )
        )
          .then(handleResponse)
          .then((response) => {
            if (response.data.roles || response.data.UserRole) {
              // authencated, do not need 2FA
              setUser(response.data)
            }
            return response.data
          })
      },
      [logInService, executeRecaptcha, logInService2FA, setUser],
    ),

    requestOTP: useCallback(
      ({ accountId, sessionId, method }) => {
        return selectService2FA({
          sessionId,
          account: accountId,
          twoFactorSource: method,
        }).then(handleResponse)
      },
      [selectService2FA],
    ),

    verifyOTP: useCallback(
      ({ accountId, sessionId, code, selectedMethod, shouldRememberDeivice }) => {
        return verifyServiceOTP({
          sessionId,
          account: accountId,
          deviceIdentity: getDeviceIdentity(),
          rememberDevice: shouldRememberDeivice,
          otpCode: code,
          twoFactorSource: selectedMethod,
        })
          .then(handleResponse)
          .then((result) => setUser(result.data))
      },
      [verifyServiceOTP, setUser],
    ),
  }

  if (site.name === "franchise") {
    return { ...franchiseAuth, fetchUser, getAuthURL, logOut }
  }
  return { fetchUser, getAuthURL, logIn, logOut }
}

export const useNotifications = () => {
  const queryOrders = usePostSales("/tw/api/order-form/get-order-list")
  const site = useSite()
  const { setUser } = useContext(UserContext)
  const user = useUser()

  const queryOverDueOrdersCount = useCallback(() => {
    return queryOrders({
      Type: "scooter",
      PromiseDeliveryDateFrom: new Date(0),
      PromiseDeliveryDateTo: getLocalISOString(new Date()),
      WorkStep: [40001, 40004, 40008, 50001],
      PaginationCriteria: {
        Offset: 0,
        Limit: 1,
      },
    }).then(handleResponse)
  }, [queryOrders])

  // TODO exhibition sales should not get-order-list
  const fetchNotifications = useCallback(
    () =>
      (site.name === "sales" && !user.Roles.some((role) => ["sales_portal_exhibition_sales"].includes(role))
        ? queryOverDueOrdersCount().then((response) => [
            {
              type: "deliveryOverdue",
              link: "/order/manage?tab=9",
              count: response.Data.TotalCount,
            },
          ])
        : Promise.resolve([])
      ).then((notifications) => {
        setUser((state) => Object.assign({}, state, { notifications }))
      }),
    [site, setUser, user, queryOverDueOrdersCount],
  )

  return { fetchNotifications }
}
